import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navigation = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:h-[72px] md:gap-8 md:px-6">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-accent-400">
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[17px] font-bold tracking-wide text-foreground-950">
              大桥未久
            </span>
            <span className="font-label mt-1 text-[9px] tracking-[0.22em] text-foreground-400">
              FILM ARCHIVE
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                [
                  "relative cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "font-medium text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-950",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={[
                      "absolute bottom-1 left-1/2 h-[2px] w-4 -translate-x-1/2 rounded-full bg-accent-500 transition-opacity",
                      isActive ? "opacity-100" : "opacity-0",
                    ].join(" ")}
                  ></span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSubmit}
          role="search"
          className="ml-auto hidden w-[260px] items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 transition-colors focus-within:border-primary-400 md:flex"
        >
          <i className="ri-search-line text-sm text-foreground-400"></i>
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索作品或资讯"
            aria-label="搜索作品或资讯"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            aria-label="提交搜索"
            className="cursor-pointer text-foreground-500 transition-colors hover:text-accent-600"
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </form>

        <Link
          to="/category/movie"
          className="ml-auto hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:ml-0 md:block"
        >
          作品全集
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="打开导航菜单"
          aria-expanded={menuOpen}
          className="ml-auto flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 md:ml-0 lg:hidden"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 lg:hidden">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-4 md:px-6">
            <form
              onSubmit={handleSubmit}
              role="search"
              className="flex w-full items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
            >
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索作品标题或资讯关键词"
                aria-label="搜索作品标题或资讯关键词"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-accent-700"
              >
                搜索
              </button>
            </form>

            <nav aria-label="移动端导航" className="mt-3 grid grid-cols-2 gap-2">
              {navigation.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    [
                      "flex min-h-[48px] cursor-pointer items-center justify-between rounded-md border px-4 text-sm transition-colors",
                      isActive
                        ? "border-primary-200 bg-primary-50 font-medium text-primary-700"
                        : "border-background-200 text-foreground-700",
                    ].join(" ")
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}